// Primitives UI condivisi: TeamMark, MetricCard, PriorityTile, ProfileField.
import { BarChart3 } from "lucide-react";
import { cn } from "@v2/lib/utils";
import { Card, CardContent } from "@v2/components/ui/card";
import { Sparkline } from "./charts";
import type { Team } from "../types";

// Stemma della squadra: il logo se c'e', altrimenti uno scudo con i suoi colori.
export function TeamCrest({ team, size = 32 }: { team: Pick<Team, "name" | "logo_url" | "primary_color" | "secondary_color">; size?: number }) {
  if (team.logo_url) {
    return <img src={team.logo_url} alt={team.name} style={{ width: size, height: size }} className="rounded-lg object-contain shrink-0" />;
  }
  return (
    <svg role="img" aria-label={team.name} width={size} height={Math.round((size * 34) / 30)} viewBox="0 0 30 34" className="shrink-0">
      <path d="M15 1 28 5.6v9.8c0 8.4-5.4 14.2-13 17.6C7.4 29.6 2 23.8 2 15.4V5.6z" fill={team.primary_color || "#005F7E"} />
      <path d="M15 1 28 5.6v9.8c0 8.4-5.4 14.2-13 17.6z" fill={team.secondary_color || "#004272"} />
    </svg>
  );
}

export function TeamMark({ team }: { team: Team }) {
  return <TeamCrest team={team} size={36} />;
}

export function MetricCard({
  label,
  value,
  subtitle,
  icon: Icon,
  accent,
  trend,
}: {
  label: string;
  value: string | number;
  subtitle?: string;
  icon?: typeof BarChart3;
  gradient?: string;
  accent?: boolean;
  trend?: number[];
}) {
  return (
    <Card className="shadow-card hover:shadow-md transition-shadow">
      <CardContent className="p-4 flex flex-col gap-1">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            {Icon && <Icon className="h-3.5 w-3.5" />}
            <span className="stat-label">{label}</span>
          </div>
          {trend && trend.length > 1 && <Sparkline values={trend} color="hsl(var(--accent))" />}
        </div>
        <span className={cn("stat-value", accent && "is-accent")}>{value}</span>
        {subtitle && <span className="stat-hint truncate">{subtitle}</span>}
      </CardContent>
    </Card>
  );
}

export function PriorityTile({ label, value, accent }: { label: string; value: number | string; accent?: string }) {
  return (
    <div className="rounded-xl border bg-card p-3 text-center min-w-0">
      <div
        className={cn(
          "text-xl sm:text-2xl font-bold leading-tight tabular-nums break-words",
          accent,
        )}
      >
        {value}
      </div>
      <div className="text-xs text-muted-foreground mt-1 capitalize break-words">{label}</div>
    </div>
  );
}

export function RankMedal({ rank }: { rank: number }) {
  const cls = rank === 1 ? "rank-1" : rank === 2 ? "rank-2" : rank === 3 ? "rank-3" : "rank-other";
  return <span className={cn("rank-medal", cls)}>{rank}</span>;
}

export function ProfileField({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border bg-card p-3">
      <p className="text-xs text-muted-foreground capitalize">{label}</p>
      <p className="font-medium text-sm break-words">{value}</p>
    </div>
  );
}
